import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, FolderClosed, Mic, MessageSquare } from 'lucide-react';
import { cn } from '@core/utils/cn';
import type { HistoryProject } from '../hooks/useHistoryTree';
import { sessionTitle } from '../utils/labels';

export interface HistoryRowProps {
  readonly entry: HistoryProject;
  /** Closes the mobile drawer once a row has been followed. */
  readonly onNavigate?: () => void;
}

const ROW =
  'group/row flex min-h-11 items-center gap-1 rounded-lg pe-1 text-sm outline-none ' +
  'transition-colors lg:min-h-9';

/**
 * One project in the rail, with the caller's own sittings folded beneath it.
 *
 * The nesting is drawn the way every sidebar draws it — a vertical guide from the
 * parent down past its children — because indentation alone stops reading as
 * containment once the list is long enough to scroll.
 *
 * Rename, pin and archive are deliberately absent rather than stubbed: the
 * contract has no user-side `PATCH /projects/:id` or `PATCH /sessions/:id`, and a
 * menu whose items do nothing is worse than a menu that is not there yet.
 */
export function HistoryRow({ entry, onNavigate }: HistoryRowProps): React.JSX.Element {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { project, sessions } = entry;
  const projectPath = `/projects/${String(project.id)}`;
  const isActive = pathname === projectPath;
  const holdsActiveSession = pathname.startsWith(`${projectPath}/sessions/`);
  // Open when the reader is already inside this project: the rail should show
  // where they are without being asked.
  const [open, setOpen] = useState(isActive || holdsActiveSession);
  const listId = `history-sessions-${String(project.id)}`;

  return (
    <li>
      <div className={cn(ROW, isActive ? 'bg-bg' : 'hover:bg-bg/70')}>
        {sessions.length > 0 ? (
          <button
            type="button"
            onClick={() => {
              setOpen((shown) => !shown);
            }}
            aria-expanded={open}
            aria-controls={listId}
            aria-label={t('assistant.toggleSessions', { project: project.name })}
            className="flex size-6 shrink-0 items-center justify-center rounded text-muted transition-colors hover:text-text"
          >
            <ChevronRight
              size={13}
              aria-hidden="true"
              className={cn('transition-transform', open ? 'rotate-90' : 'rtl:rotate-180')}
            />
          </button>
        ) : (
          // Keeps the titles of childless projects on the same optical line as
          // the rest rather than jogging left by the width of a chevron.
          <span className="size-6 shrink-0" aria-hidden="true" />
        )}

        <Link
          to={projectPath}
          onClick={onNavigate}
          className="flex min-w-0 flex-1 items-center gap-2.5 py-1.5 text-start outline-none"
          aria-current={isActive ? 'page' : undefined}
        >
          <FolderClosed
            size={15}
            aria-hidden="true"
            className={cn('shrink-0', isActive ? 'text-accent' : 'text-muted')}
          />
          <span className={cn('truncate', isActive && 'font-medium')}>{project.name}</span>
        </Link>

        {project.level === 'read_write' ? (
          <Link
            to={`${projectPath}/interview`}
            onClick={onNavigate}
            // Visible on hover on a pointer, always visible to the keyboard —
            // focus-within would hide it from the very users who cannot hover.
            className={cn(
              'flex size-7 shrink-0 items-center justify-center rounded-md text-muted opacity-0',
              'transition hover:bg-card hover:text-accent focus-visible:opacity-100',
              'dda-focus group-hover/row:opacity-100',
            )}
            aria-label={t('assistant.newSitting', { project: project.name })}
            title={t('assistant.newSitting', { project: project.name })}
          >
            <Mic size={14} aria-hidden="true" />
          </Link>
        ) : null}
      </div>

      {open && sessions.length > 0 ? (
        <ul id={listId} className="ms-[1.35rem] mt-0.5 space-y-0.5 border-s border-line ps-2.5">
          {sessions.map((session) => {
            const sessionPath = `${projectPath}/sessions/${String(session.id)}`;
            const active = pathname === sessionPath;
            return (
              <li key={session.id}>
                <Link
                  to={sessionPath}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex min-h-10 items-center gap-2 rounded-lg px-2 py-1.5 text-sm outline-none',
                    'transition-colors dda-focus lg:min-h-8',
                    active ? 'bg-bg text-text' : 'text-muted hover:bg-bg/70 hover:text-text',
                  )}
                >
                  <MessageSquare size={13} aria-hidden="true" className="shrink-0" />
                  <span className="truncate">{sessionTitle(session, t)}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}
